import { useEffect, useState } from 'react'
import React from 'react'

import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { AppModals, UsercentricsQuickAction } from 'modules/desktop/components'
import { SttQuickAction } from 'modules/desktop/modules/stt'
import { useNavigate } from 'react-router-dom'
import { appConfigs } from 'shared/configs/appConfigs'
import { useCommonStore } from 'shared/stores/commonStore'
import { AppConfig } from 'shared/types/app'
import { MainLayout } from 'src/shared/components'
import { useTopDockHeight } from 'src/shared/components/MainLayout/hooks/useTopDockHeight'
import { getIsInsideWindow } from 'src/shared/utils'

interface Props {
  appConfig: AppConfig
}

// A very simple hook to force a re-render when a dependency changes
function useForceRerender(dependency: unknown) {
  const [, setToggle] = useState(false)
  useEffect(() => () => setToggle(toggle => !toggle), [dependency])
}

const DetachedAppPage: React.FC<Props> = ({ appConfig }) => {
  const navigate = useNavigate()
  const Component = appConfig.component!
  const { height: topDockHeight, topDockRef } = useTopDockHeight()
  const isWindowMode = getIsInsideWindow()
  const setIsDetached = useCommonStore(state => state.setIsDetached)
  const { apps: availableApps } = useAvailableApps()

  useEffect(() => {
    setIsDetached(true)
    return () => {
      setIsDetached(false)
    }
  }, [setIsDetached])

  // force re-render to make sure router is refreshed on app switch
  useForceRerender(appConfig.id)

  return (
    <MainLayout
      topDockHeight={topDockHeight}
      topDockRef={topDockRef}
      quickActions={
        <>
          <SttQuickAction />
          <UsercentricsQuickAction />
        </>
      }
      handleAppClick={app => {
        if (
          Object.values(availableApps).some(
            availableApp =>
              availableApp.id === app.id && availableApp.isDisabled
          )
        ) {
          return
        }

        const relativePath = app.url!.replace(
          process.env.REACT_APP_CONTAINER_DOMAIN!,
          ''
        )
        navigate(relativePath)
      }}
    >
      <Component />

      {!isWindowMode &&
        Object.entries(appConfigs).map(([id, config]) => {
          return config?.modals ? (
            <AppModals key={`${id}-modals`} appConfig={config} />
          ) : null
        })}
    </MainLayout>
  )
}

export { DetachedAppPage }
